<template>
  <div id="side-bar" class="side-bar bg-light shadow-sm">
    <div class="top-menu scroll-style">
      <ul class="parent-menu" v-if="checkAndLoad()">
        <li v-for="(i, index) in userNavigationItems" :key="'sb-' + index" :class="{ hover: i.isOpen }">
          <span v-if="i.routeName">
            <router-link :to="getLink(i)">
              <div>{{ i.display }}</div>
            </router-link>
          </span>
          <span v-else>
            <div class="menu-item" @click="i.isOpen = !i.isOpen">
              <div class="icon-menu me-2">
                <i class="bi bi-chevron-double-down" v-show="!i.isOpen"></i>
                <i class="bi bi-chevron-double-up" v-show="i.isOpen"></i>
              </div>
              {{ i.display }}
            </div>
          </span>
          <div class="child-menu">
            <ul v-if="i.children.length > 0">
              <li v-for="(c, index) in i.children" :key="'sbc-' + index">
                <router-link :to="getLink(c)">
                  <div>{{ c.display }}</div>
                </router-link>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
    <div class="bottom-menu">
      <ul class="parent-menu">
        <li>
          <a href="#" @click="checkForUpdates"><i class="bi bi-arrow-clockwise"></i> Check for Updates</a>
        </li>
        <li>
          <a href="https://docs.eps-office.com/Clockwork/LogginOn" target="_doc"> <i class="bi bi-info-circle"></i> Documentation </a>
          <a v-show="store.state.user.role == 20" href="https://eps-software.visualstudio.com/_git/EPSDocs?path=/Clockwork/LoggingOn.md&version=GBmaster&_a=contents" target="_docedit">
            <i class="bi bi-pencil" title="Edit Documentation"></i
          ></a>
        </li>
        <li v-if="store.state.user.email">
          <router-link to="/profile"> <i class="bi bi-person"></i>Profile </router-link>
        </li>
        <li v-if="store.state.user.email">
          <button class="btn btn-link" @click="logOut"><i class="bi bi-unlock"></i> Log Out</button>
        </li>
        <li v-if="!store.state.user.email">
          <router-link to="/login">
            <div><i class="bi bi-lock"></i> Log In</div>
          </router-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { computed, inject, ref, onMounted, watch, toRaw } from "vue";
import router from "@/router";
import * as _utility from "@/utility";
import * as _clockworkService from "@/services/clockworkService";
import * as _securityService from "@/services/securityService";

export default {
  setup() {
    const store = inject("store");
    const isInitializing = ref(true);
    const userNavigationItems = ref([]);
    const isFetching = ref(false);

    const normalizeItems = (items) =>
      (Array.isArray(items) ? items : []).map((i) => ({
        display: i.display ?? "",
        routeName: i.routeName ?? null,
        parameterId: i.parameterId ?? null,
        parameter: i.parameter ?? null,
        isOpen: !!i.isOpen,
        roles: i.roles != null ? [].concat(i.roles) : [],
        divisions: i.divisions != null ? [].concat(i.divisions) : [],
        children: normalizeItems(i.children),
      }));

    const checkAndLoad = async () => {
      if (!_utility.retrieveSearch("user")) return false;
      const user = JSON.parse(_utility.retrieveSearch("user"));
      //  If we already have items in memory, skip fetch
      if (userNavigationItems.value.length) {
        const currentRole = user.role;
        const firstItemRole = userNavigationItems.value[0]?.roles;
        const currentDivision = user.division;
        const firstItemDivision = userNavigationItems.value[0]?.divisions;
        //check if division or role changed if so clear cache so it will rerun getMenu()
        if (currentRole != firstItemRole || currentDivision != firstItemDivision) {
          _utility.storeSearch("navigationItems", "", false);
          userNavigationItems.value = [];
          return await checkAndLoad();
        } else return true;
      }
      // check cached data
      const stored = _utility.retrieveSearch("navigationItems");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.length) {
            userNavigationItems.value = normalizeItems(parsed);
            return true;
          }
        } catch (err) {
          console.error("Failed to parse stored navigationItems:", err);
        }
      }

      // no cached items
      const userId = user.id;
      if (!userId) return false;

      if (isFetching.value) return false;
      isFetching.value = true;
      try {
        const response = await _clockworkService.getMenu();
        if (response && response.success) {
          _utility.storeSearch("navigationItems", JSON.stringify(response.menus), false);
          userNavigationItems.value = normalizeItems(response.menus);
          return userNavigationItems.value.length > 0;
        } else {
          console.warn("getMenu returned no success:", response);
          return false;
        }
      } catch (err) {
        console.error("Error fetching menu:", err);
        return false;
      } finally {
        isFetching.value = false;
      }
    };

    const getLink = (item) => {
      if (!item.routeName) return "";
      if (item.url && item.url.length > 0) return item.url;
      return item.parameterId != null ? { name: item.routeName, params: { id: item.parameter } } : { name: item.routeName };
    };

    const isInRole = (menuItem) => _utility.isInRole(menuItem.roles) && _utility.isInDivision(menuItem.divisions);

    const currentRoute = router;
    const logOut = () => {
      _securityService.logOut().then((response) => {
        if (response.success) {
          store.dispatch("logOut").then(() => currentRoute.push("/login"));
        }
      });
    };

    const checkForUpdates = function () {
      if (window.updateManager) {
        window.updateManager.checkForUpdates();

        // Show feedback to user
        store.dispatch("showToast", {
          message: "Checking for updates...",
          type: "info",
          duration: 2000,
        });
      }
    };

    return {
      checkForUpdates,
      userNavigationItems,
      checkAndLoad,
      normalizeItems,
      isInitializing,
      isFetching,
      getLink,
      isInRole,
      logOut,
      store,
    };
  },
};
</script>

<style scoped>
.icon-menu {
  position: absolute;
  right: 0;
}
.top-menu {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: calc(100vh - 250px);
  overflow-x: hidden;
  overflow-y: auto;
}
.bottom-menu {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.075);
}
.bottom-menu ul li i {
  margin-right: 5px;
}
.menu-item {
  color: var(--bs-primary);
  padding-left: 10px;
  height: 34px;
  line-height: 34px;
}
.menu-item:hover,
.hover .menu-item,
.hover .menu-item .bi {
  color: var(--bs-white);
  border-bottom: 1px solid var(--bs-primary);
}
.menu-item .bi {
  border: none !important;
}
.child-menu {
  max-height: 0;
  transition: max-height 0.25s ease-out;
  overflow: hidden;
}
.hover .child-menu {
  max-height: 600px;
  transition: max-height 0.4s ease-in;
}

.side-bar .child-menu ul li {
  background-color: rgb(223, 223, 223) !important;
  color: var(--bs-primary);
}

.side-bar .child-menu ul li:hover,
.side-bar .child-menu ul li:hover a,
.side-bar .child-menu ul li:hover button.btn-link {
  background-color: rgb(160, 160, 160) !important;
  color: var(--bs-white);
}
.side-bar .child-menu ul li,
.side-bar .child-menu ul li a,
.side-bar .child-menu ul li button.btn-link {
  color: var(--bs-primary);
}
.side-bar .child-menu ul li a.router-link-active {
  color: var(--bs-dark);
  font-weight: bold;
}
.bottom-menu i.bi {
  position: absolute;
  right: 10px;
}

.bottom-menu i.bi.bi-pencil {
  right: 40px;
}
.bottom-menu a {
  margin-left: 10px;
}
.bottom-menu li {
  height: 35px;
}
</style>
